// Coach's Readiness Rate report: how a client's pre-training questionnaires compare with their
// own normal (their previous 6 weeks), over a period. Everything is calculated here from the daily
// stats and the training days the controller loaded (see utils/readiness-report).

import moment from "moment";
import PropTypes from "prop-types";
import { useCallback, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { AthleteLinks } from "../../../../parts/athlete-links";
import BackButton from "../../../../parts/BackButton";
import { AthleteSelect } from "../../../../parts/inputs/athlete-select-new";
import SvgIcon from "../../../../parts/SvgIcon";
import { RillContext } from "../../../../rill/RillReact/share/base";
import getAvatarUrl from "../../../../utils/get-avatar-url";
import {
  buildReadinessCsv,
  buildReadinessReport,
  firstQuestionnaireUtc,
} from "../../../../utils/readiness-report";
import NavigationComponent from "../components/NavigationComponent";
import { ReportsBackHandler } from "../components/ReportsBackHandler";
import RangeContainerComponent from "./RangeContainerComponent";
import { ReadinessDailyTable } from "./ReadinessDailyTable";
import { ReadinessEmptyState } from "./ReadinessEmptyState";
import { ReadinessInsights } from "./ReadinessInsights";
import { ReadinessKpiTiles } from "./ReadinessKpiTiles";
import { ReadinessQuestionCards } from "./ReadinessQuestionCards";
import { ReadinessQuestionChart } from "./ReadinessQuestionChart";
import { ReadinessScoreChart } from "./ReadinessScoreChart";
import { useIntensity } from "./readinessFormat";

const DATE_CALENDAR = "MMM D, YYYY";
const DATE_UTC = "YYYYMMDD";
const DEFAULT_RANGE = { type: "weeks", value: 2 };

const toUtcNumber = (date) => Number(moment(date).format(DATE_UTC));

// The first day of a "last N weeks/months" period, counting today (so 2 weeks is 14 days)
const quickRangeStart = (end, { type, value }) =>
  end.clone().subtract(value, type).add(1, "day");

const firstName = (athlete) => athlete?.name?.trim().split(/\s+/)[0] || null;

// Builds the CSV only when the coach asks for it, and hands it to the browser as a file
const downloadCsv = (report, athlete, isRiR) => {
  const who = (firstName(athlete) ?? "me")
    .toLowerCase()
    .replaceAll(/[^a-z0-9]+/g, "-");
  const { fromUtc, toUtc } = report.period;
  const url = URL.createObjectURL(
    new Blob([buildReadinessCsv(report, { isRiR })], {
      type: "text/csv;charset=utf-8",
    })
  );
  const link = document.createElement("a");
  link.href = url;
  link.download = `readiness-rate-${who}-${fromUtc}-${toUtc}.csv`;
  link.click();
  URL.revokeObjectURL(url);
};

/**
 * Gets the tracking requirements URL.
 * @param {*} athlete
 * @param {*} organizationCoach
 * @returns
 */
const trackingRequirementsUrl = (athlete, organizationCoach) => {
  if (!athlete?._id) return null;
  const base = `/athlete-portal/profile/?athlete=${athlete._id}&section=client-configuration`;
  return organizationCoach
    ? `${base}&organizationCoach=${organizationCoach}`
    : base;
};

// Everything under the filters: the empty states, or the insights, tiles, chart and questions
const ReadinessReportBody = ({ report, athleteName, trackingUrl }) => {
  const { t } = useTranslation("athlete-portal");

  if (report.state === "noData") {
    return (
      <ReadinessEmptyState
        kind="noData"
        athleteName={athleteName}
        trackingUrl={trackingUrl}
      />
    );
  }
  // A period can have no questionnaire and still have training days the client missed: that is
  // worth showing (completion, missed days, the chart's rings and the log), minus what needs answers
  const hasQuestionnaires = report.state !== "empty";
  const hasMissedDays = report.days.length > 0;

  return (
    <>
      <ReadinessInsights insights={report.insights} athleteName={athleteName} />
      {hasQuestionnaires ? null : <ReadinessEmptyState kind="period" />}
      {hasQuestionnaires || hasMissedDays ? (
        <>
          <h3 className="rr-section">{t("readinessRate.report.thisPeriod")}</h3>
          <ReadinessKpiTiles report={report} />
          <ReadinessScoreChart report={report} athleteName={athleteName} />
          {hasQuestionnaires ? (
            <>
              <ReadinessQuestionChart report={report} />
              <h3 className="rr-section">
                {t("readinessRate.report.byQuestion.title")}
              </h3>
              <ReadinessQuestionCards report={report} />
            </>
          ) : null}
          <h3 className="rr-section">{t("readinessRate.report.dailyLog")}</h3>
          <ReadinessDailyTable report={report} athleteName={athleteName} />
        </>
      ) : null}
    </>
  );
};

ReadinessReportBody.propTypes = {
  report: PropTypes.object.isRequired,
  athleteName: PropTypes.string,
  trackingUrl: PropTypes.string,
};

/**
 * Renders the readiness rate report.
 * @returns
 */
const ReadinessRate = () => {
  const { t } = useTranslation("athlete-portal");
  const { locals } = useContext(RillContext);
  const { isRiR } = useIntensity();
  const { query, pathname, athlete } = locals;
  const dailyStats = locals.dailyStats ?? [];
  const trainingDays = locals.trainingDays ?? [];

  // One "today" per mount: every calculation uses the same one
  const today = useMemo(() => moment().startOf("day"), []);
  // Opened from a calendar day: the period ends on that day so the clicked questionnaire is in it
  const [range, setRange] = useState(() => {
    const day = moment(query.date, "YYYY-MM-DD", true);
    const endsOnDay = day.isValid() && day.isBefore(today, "day");
    return {
      dateFrom: quickRangeStart(endsOnDay ? day : today, DEFAULT_RANGE).format(
        DATE_CALENDAR
      ),
      dateTo: endsOnDay ? day.format(DATE_CALENDAR) : null,
      quickDisabled: false,
      rangeSelector: DEFAULT_RANGE,
    };
  });

  // The quick buttons count back from the end of the period; the selector hands over the new
  // state and this keeps "last 2 weeks" at 14 days, not 15.
  const updateState = useCallback(
    (patch) =>
      setRange((previous) => {
        const next = { ...previous, ...patch };
        const quick = patch.rangeSelector;
        if (quick && quick.type !== "lifetime") {
          const end = next.dateTo ? moment(next.dateTo, DATE_CALENDAR) : today;
          next.dateFrom = quickRangeStart(end, quick).format(DATE_CALENDAR);
        }
        return next;
      }),
    [today]
  );
  /**
   * Gets the date of the first entry.
   * @returns
   */
  const getDateFirstEntry = useCallback(() => {
    const first = firstQuestionnaireUtc(dailyStats);
    return moment(first === null ? today : String(first), DATE_UTC).format(
      "YYYY-MM-DD"
    );
  }, [dailyStats, today]);

  const isLifetime =
    range.rangeSelector.type === "lifetime" && !range.quickDisabled;
  const fromUtc = isLifetime
    ? null
    : toUtcNumber(moment(range.dateFrom, DATE_CALENDAR));
  const toUtc = range.dateTo
    ? toUtcNumber(moment(range.dateTo, DATE_CALENDAR))
    : toUtcNumber(today);

  /**
   * Builds the readiness report.
   * @returns
   */
  const report = useMemo(
    () =>
      buildReadinessReport({
        dailyStats,
        trainingDays,
        fromUtc,
        toUtc,
        todayUtc: toUtcNumber(today),
      }),
    [dailyStats, trainingDays, fromUtc, toUtc, today]
  );

  const hasRows = report.days.length > 0;

  const athleteName = firstName(athlete);
  const isCoachView = Boolean(athlete);

  return (
    <>
      <div className="athlete-section-header only-show-mobile">
        <AthleteLinks
          athlete={athlete}
          pathName={pathname}
          classNames={"only-show-mobile"}
          query={query}
        />
        <div className="athlete-select dropdown-athlete">
          <div
            className="avatar"
            style={{ backgroundImage: `url(${getAvatarUrl(athlete)})` }}
          />
          <AthleteSelect locals={locals} />
        </div>
      </div>
      <main
        role="reports-wrapper" //NOSONAR
      >
        <NavigationComponent
          query={query}
          textKey="nav.calendarDayView"
          pathName={pathname}
          classNames="mobile-hidden"
        />

        <article
          role="report-content" //NOSONAR
          className="rr-report"
        >
          <div className="rr-titlebar">
            <hgroup className="flex gap-2 items-center mb-2 justify-start!">
              {query.showBack && <BackButton />}
              <h2 className="mobile-hidden">{t("readinessRate.title")}</h2>
            </hgroup>
            {hasRows ? (
              <button
                type="button"
                className="rr-export"
                data-button-type="secondary"
                onClick={() => downloadCsv(report, athlete, isRiR)}
              >
                <SvgIcon src="/img/new-icons/export.svg" />
                {t("readinessRate.report.exportCsv")}
              </button>
            ) : null}
          </div>
          <p className="rr-subtitle mobile-hidden">
            {isCoachView
              ? t("readinessRate.report.subtitleCoach", { name: athleteName })
              : t("readinessRate.report.subtitleSelf")}
          </p>
          <ReportsBackHandler
            title={t("readinessRate.title")}
            locals={locals}
          />

          <RangeContainerComponent
            {...range}
            updateState={updateState}
            getDateFirstEntry={getDateFirstEntry}
            activeButtonType="outline"
            locals={locals}
          />

          <ReadinessReportBody
            report={report}
            athleteName={isCoachView ? athleteName : null}
            trackingUrl={
              isCoachView
                ? trackingRequirementsUrl(athlete, query.organizationCoach)
                : null
            }
          />
        </article>
      </main>
    </>
  );
};

export default ReadinessRate;
